import * as React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import {
  AppBar,
  Avatar,
  Box,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  FormControl,
  TextField,
  Tab,
  Tabs,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "../img/Logout.png";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LangIcon from "../img/Globe.png";
import InputAdornment from "@mui/material/InputAdornment";
import { useLocation, useNavigate } from "react-router-dom";
import { headingPage } from "../utils/headingList";
import { googleLogout } from "@react-oauth/google";
import { useTranslation } from "react-i18next";

const NavbarRoot = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: "#5C5C5C",
}));

const SingOut = async () => {};
const styles = {
  appBarIcon: {
    width: 20,
    height: 20,
  },
};

const StyledMenu = styled(Menu, {
  shouldForwardProp: (prop) => prop !== "borderRadius",
})(({ theme }) => ({
  borderRadius: "15px", // Adjust the value as needed
  "& .MuiPaper-root": {
    // Target the paper element within Menu
    borderRadius: "inherit", // Inherit the border radius from StyledMenu
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: "#5C5C5C", // Adjust as desired
}));

function samePageLinkNavigation(event) {
  if (
    event.defaultPrevented ||
    event.button !== 0 || // ignore everything but left-click
    event.metaKey ||
    event.ctrlKey ||
    event.altKey ||
    event.shiftKey
  ) {
    return false;
  }
  return true;
}

function LinkTab(props) {
  return (
    <Tab
      component="a"
      onClick={(event) => {
        // Routing libraries handle this, you can remove the onClick handle when using them.
        if (samePageLinkNavigation(event)) {
          event.preventDefault();
        }
      }}
      aria-current={props.selected && "page"}
      {...props}
      sx={{
        textTransform: "none",
      }}
    />
  );
}

LinkTab.propTypes = {
  selected: PropTypes.bool,
};

export default function BaseAppBar(props) {
  const [t, i18n] = useTranslation("global");

  const navigate = useNavigate(); // Hook to programmatically navigate
  const { onSidebarOpen, ...other } = props;
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [userData, setUserData] = React.useState(
    JSON.parse(sessionStorage.getItem("authUserData"))
  );

  const settings = [
    {
      name: t("appbar.logout"),
      component: SingOut,
      icon: <img key="1" src={LogoutIcon} style={styles.appBarIcon} />,
    },
  ];

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const location = useLocation();
  // Access URL components
  const pathname = location.pathname; // Get the pathname (path after the domain)
  const pathArr = pathname.split("/")[1]; // Remove "/"
  const isHome = pathArr == "home" ? true : false;
  const team = pathname.split("/")[2]; // Get team from URL parameter

  const [language, setLanguage] = React.useState("en");

  const handleChange = (event) => {
    setLanguage(event.target.value);
    i18n.changeLanguage(event.target.value);
  };

  // Tab --------------------
  const [activeTab, setActiveTab] = React.useState(0);

  const handleChangeTab = (event, newValue) => {
    // event.type can be equal to focus with selectionFollowsFocus.
    if (
      event.type !== "click" ||
      (event.type === "click" && samePageLinkNavigation(event))
    ) {
      setActiveTab(newValue);
      switch (newValue) {
        case 0:
          navigate("/user");
          break;
        case 1:
          navigate("/log");
          break;
        default:
          navigate("/");
      }
    }
  };

  React.useEffect(() => {
    if (pathArr === "log") {
      setActiveTab(1);
    } else if (pathArr === "user") {
      setActiveTab(0);
    } else if (pathArr === "grounding") {
      setActiveTab(1);
    } else if (pathArr === "boq") {
      // Verify team from URL parameter
      if (team === "lbd") {
        setActiveTab(0);
      } else if (team === "lbm-lg") {
        setActiveTab(1);
      } else if (team === "lbm-id") {
        setActiveTab(2);
      } else if (team === "ied") {
        setActiveTab(3);
      } else if (team === "up-power") {
        setActiveTab(4);
      } else if (team === "up-water") {
        setActiveTab(5);
      } else if (team === "moblx") {
        setActiveTab(6);
      }
    } else {
      setActiveTab(0);
    }
  }, [pathArr, team]);
  //---------------------------- END Tab

  const handleChangeTabProduct = (event, newValue) => {
    // event.type can be equal to focus with selectionFollowsFocus.
    if (
      event.type !== "click" ||
      (event.type === "click" && samePageLinkNavigation(event))
    ) {
      setActiveTab(newValue);
      switch (newValue) {
        case 0:
          navigate("/product");
          break;
        case 1:
          navigate("/grounding");
          break;
        default:
          navigate("/");
      }
    }
  };

  const handleChangeTabBoq = (event, newValue) => {
    // event.type can be equal to focus with selectionFollowsFocus.
    if (
      event.type !== "click" ||
      (event.type === "click" && samePageLinkNavigation(event))
    ) {
      setActiveTab(newValue);

      switch (newValue) {
        case 0:
          navigate("/boq/lbd");
          break;
        case 1:
          navigate("/boq/lbm-lg");
          break;
        case 2:
          navigate("/boq/lbm-id");
          break;
        case 3:
          navigate("/boq/ied");
          break;
        case 4:
          navigate("/boq/up-power");
          break;
        case 5:
          navigate("/boq/up-water");
          break;
        case 6:
          navigate("/boq/moblx");
          break;
        default:
          navigate("/");
      }
    }
  };

  const handleLogout = () => {
    googleLogout();
    sessionStorage.clear();
    console.log("User logged out");
    // Optional: Refresh the page to reflect the logout
    window.location.reload();
  };

  return (
    <>
      <NavbarRoot
        sx={{
          left: {
            lg: isHome ? 0 : 60,
            xs: 0,
          },
          width: {
            lg: isHome ? "calc(100%)" : "calc(100% - 60px)",
            xs: "calc(100%)",
          },
          boxShadow: isHome ? "none" : 2,
        }}
        {...other}
      >
        <Toolbar
          disableGutters
          sx={{
            left: 0,
            px: 3,
            backgroundColor: isHome ? "#1D589D" : "",
          }}
        >
          <IconButton
            onClick={onSidebarOpen}
            sx={{
              display: {
                xs: "inline-flex",
                lg: "none",
              },
            }}
          >
            <MenuIcon
              fontSize="medium"
              sx={{ color: isHome ? "white" : "gray" }}
            />
          </IconButton>

          {headingPage.map((item, index) => {
            return item.path === pathArr ? (
              item.title === "setting" ? (
                <div key={index} style={{ display: "contents" }}>
                  <Typography variant="h5">
                    <Box sx={{ marginRight: 6 }}>
                      {t(`appbar.title.${item.title}`)}
                    </Box>
                  </Typography>
                  <Tabs
                    value={activeTab > 1 ? 0 : activeTab}
                    onChange={handleChangeTab}
                  >
                    <LinkTab
                      label={t("user.title")}
                      style={{ fontSize: "18px" }}
                    />
                    <LinkTab
                      label={t("logging.title")}
                      style={{ fontSize: "18px" }}
                    />
                  </Tabs>
                </div>
              ) : item.title === "product" ? (
                <div key={index} style={{ display: "contents" }}>
                  <Typography variant="h5">
                    <Box sx={{ marginRight: 6 }}>
                      {t(`appbar.title.${item.title}`)}
                    </Box>
                  </Typography>
                  <Tabs
                    value={activeTab > 1 ? 0 : activeTab}
                    onChange={handleChangeTabProduct}
                  >
                    <LinkTab
                      label={t("product.title")}
                      style={{ fontSize: "18px" }}
                    />
                    <LinkTab
                      label={t("grounding.title")}
                      style={{ fontSize: "18px" }}
                    />
                  </Tabs>
                </div>
              ) : item.title === "boq" ? (
                <div key={index} style={{ display: "contents" }}>
                  <Typography variant="h5">
                    <Box sx={{ marginRight: 6 }}>
                      {t(`appbar.title.${item.title}`)}
                    </Box>
                  </Typography>
                  <Tabs value={activeTab} onChange={handleChangeTabBoq}>
                    <LinkTab label="LBD" style={{ fontSize: "18px" }} />
                    <LinkTab label="LBM(LG)" style={{ fontSize: "18px" }} />
                    <LinkTab label="LBM(ID)" style={{ fontSize: "18px" }} />
                    <LinkTab label="IED" style={{ fontSize: "18px" }} />
                    <LinkTab label="UP POWER" style={{ fontSize: "18px" }} />
                    <LinkTab label="UP WATER" style={{ fontSize: "18px" }} />
                    <LinkTab label="MOBLX" style={{ fontSize: "18px" }} />
                  </Tabs>
                </div>
              ) : (
                <Typography key={index} variant="h5">
                  {t(`appbar.title.${item.title}`)}
                </Typography>
              )
            ) : (
              ""
            );
          })}

          <Box sx={{ flexGrow: 1 }} />
          <Box>
            <FormControl sx={{ minWidth: 130 }}>
              <TextField
                select
                size="small"
                value={language}
                onChange={handleChange}
                InputProps={{
                  style: {
                    borderRadius: "15px",
                    backgroundColor: isHome
                      ? "white"
                      : `rgba(205, 225, 255, 0.47)`,
                    fontSize: "small",
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={LangIcon} alt="lang" />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="en">{t("appbar.lang.en")}</MenuItem>
                <MenuItem value="th">{t("appbar.lang.th")}</MenuItem>
              </TextField>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 1,
              marginLeft: 4,
            }}
          >
            <Typography variant="body2" color={isHome ? "white" : ""}>
              {userData && userData.name}
            </Typography>
            <Typography variant="caption" color={isHome ? "white" : ""}>
              {userData && userData.email}
            </Typography>
          </Box>
          <Avatar
            sx={{
              height: 40,
              width: 40,
              ml: 1,
            }}
          >
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu}>
                <AccountCircleIcon
                  sx={{
                    p: 0,
                    color: isHome ? "white" : "#5C5C5C",
                    bgcolor: isHome ? "#1D589D" : "#ffff",
                    height: 50,
                    width: 50,
                  }}
                />
              </IconButton>
            </Tooltip>
            <StyledMenu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting, index) => (
                <StyledMenuItem key={index}>
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    onClick={handleLogout}
                  >
                    <Grid item>
                      <Typography textAlign="center" marginX={0.5}>
                        {setting.name}
                      </Typography>
                    </Grid>
                    <Grid item>{setting.icon}</Grid>
                  </Grid>
                </StyledMenuItem>
              ))}
            </StyledMenu>
          </Avatar>
        </Toolbar>
      </NavbarRoot>
    </>
  );
}

NavbarRoot.propTypes = {
  onSidebarOpen: PropTypes.func,
};
